import { Button } from "@/modern/components/ui/button-shadcn";
import { Input } from "@/modern/components/ui/input";
import { Copy } from "lucide-react";
import { useFileDetailModal } from "../context/FileDetailModalContext";
import { Label } from "@/modern/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/modern/components/ui/select";
import { checkGrantOrder } from "@/modern/utils";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";

export const SharePermissionsSection = () => {
  const { selectedBoard, project } = useSwivuBryntumContext();
  const { file, canEditFile, swivuAppGrants } = useFileDetailModal();
  const [shareLink, setShareLink] = useState("");

  useEffect(() => {
    const resourceAgendaRecord = project?.resourceAgendaStore?.first;
    if (resourceAgendaRecord) {
      // @ts-expect-error - Bryntum model types
      const fullURL = (resourceAgendaRecord?.url || "");
      setShareLink(`${fullURL}/file/${file?.id}`);
    }
  }, [project, selectedBoard, file])

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success("Share link copied to clipboard!");
    } catch (error) {
      console.error('Failed to copy link:', error);
      toast.error('Failed to copy link');
    }
  };

  if (!file) return null;

  return (
    <div className="p-4 space-y-4">
      <p className="text-xs text-muted-foreground mb-2">Use roles to decide who can view & edit this file.</p>
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Label className="text-sm font-normal text-muted-foreground whitespace-nowrap min-w-[80px]">Can Edit:</Label>
          <Select
            disabled={!canEditFile || file.isCCImage}
            value={file?.canEditGrant ? String(file.canEditGrant) : ""}
            onValueChange={(value) => {
              if (!file?.record) return;

              const newCanEdit = Number(value);
              const currentCanView = file.record.canView || 0;

              // Check if canEdit is higher than canView, and auto-correct canView if needed
              checkGrantOrder({
                canEdit: newCanEdit,
                canView: currentCanView,
                onViewPermissionError: (canEdit) => {
                  // Auto-correct canView to match canEdit
                  if (file.record) {
                    file.record.set({
                      canEdit: canEdit,
                      canView: canEdit
                    });
                  }
                }
              });

              // If validation passed, just update canEdit
              if (newCanEdit <= currentCanView) {
                file.record.set({ canEdit: newCanEdit });
              }
            }}
          >
            <SelectTrigger className="flex-1 h-9 bg-muted rounded-md">
              <SelectValue placeholder={file.isCCImage ? "Can't set permissions for CC Files" : "Select a permission"} />
            </SelectTrigger>
            <SelectContent>
              {swivuAppGrants.map((grant) => (
                <SelectItem key={grant.value} value={String(grant.value)}>
                  {grant.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-3">
          <Label className="text-sm font-normal text-muted-foreground whitespace-nowrap min-w-[80px]">Can View:</Label>
          <Select
            disabled={!canEditFile || file.isCCImage}
            value={file?.canViewGrant ? String(file.canViewGrant) : ""}
            onValueChange={(value) => {
              if (!file?.record) return;

              const newCanView = Number(value);
              const currentCanEdit = file.record.canEdit || 0;

              // Validate that canView is not higher than canEdit
              const isValid = checkGrantOrder({
                canEdit: currentCanEdit,
                canView: newCanView
              });

              // Only update if validation passed
              if (isValid) {
                file.record.set({ canView: newCanView });
              }
            }}
          >
            <SelectTrigger className="flex-1 h-9 bg-muted rounded-md">
              <SelectValue placeholder={file.isCCImage ? "Can't set permissions for CC Files" : "Select a permission"} />
            </SelectTrigger>
            <SelectContent>
              {swivuAppGrants.map((grant) => (
                <SelectItem key={grant.value} value={String(grant.value)}>
                  {grant.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Share link */}
      <div className="space-y-2 pt-2 border-t border-border/50">
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Share link</Label>
        <div className="flex gap-2">
          <Input value={shareLink} readOnly className="flex-1 text-sm bg-muted/50" />
          <Button type="button" variant="outline" size="sm" onClick={handleCopyLink} className="h-11 shrink-0">
            <Copy className="h-4 w-4 mr-2" />
            Copy
          </Button>
        </div>
      </div>
    </div>
  );
};
